// Default template settings for PDF Proposal
// This file contains the default structure that will be used when no saved settings exist

export const DEFAULT_TEMPLATE_SETTINGS = {
  version: "2.0",
  
  // Layout settings
  layout: {
    pageMargins: { top: 20, right: 20, bottom: 20, left: 20 },
    defaultFontFamily: "times", // garamond maps to times in jsPDF
    defaultFontColor: "#000000",
  },
  
  // Header top-right block
  headerTopRight: {
    visible: true,
    preparedLine: {
      text: "Prepared for Mr. {{guest_name}}",
      fontSize: 10,
      fontWeight: "normal",
      fontColor: "#666666",
      alignment: "right",
      marginBottom: 2,
    },
    locationDateLine: {
      text: "Rome {{today_date}}",
      fontSize: 10,
      fontWeight: "normal",
      fontColor: "#666666",
      alignment: "right",
      marginBottom: 0,
    },
    blockMarginBottom: 10,
  },
  
  // Brand header (centered)
  brandHeader: {
    visible: true,
    title: {
      text: "Suite Lucina",
      fontSize: 24,
      fontWeight: "bold",
      fontColor: "#000000",
      alignment: "center",
      marginBottom: 4,
    },
    subtitle: {
      text: "Piazza di San Lorenzo in Lucina 21, Roma",
      fontSize: 10,
      fontWeight: "normal",
      fontColor: "#888888",
      alignment: "center",
      marginBottom: 15,
    },
  },
  
  // Document title (centered)
  documentTitle: {
    visible: true,
    text: "Accommodation Proposal",
    fontSize: 16,
    fontWeight: "bold",
    fontColor: "#000000",
    alignment: "center",
    marginBottom: 15,
  },
  
  // Intro section (left aligned)
  intro: {
    visible: true,
    offerValid: {
      visible: true,
      text: "Offer valid 24 h",
      fontSize: 11,
      fontWeight: "bold",
      fontColor: "#000000",
      alignment: "left",
      marginBottom: 8,
    },
    bodyText: {
      visible: true,
      text: "We are pleased to present your accommodation proposal at Suite Lucina.",
      fontSize: 10,
      fontWeight: "normal",
      fontColor: "#000000",
      alignment: "left",
      marginBottom: 15,
    },
  },
  
  // Stay Details section
  stayDetails: {
    visible: true,
    sectionTitle: {
      text: "Stay Details",
      fontSize: 12,
      fontWeight: "bold",
      fontColor: "#000000",
      alignment: "left",
      marginBottom: 8,
    },
    checkInOutLine: {
      format: "Check-in: {{check_in}} | Check-out: {{check_out}} | {{nights}} nights",
      fontSize: 10,
      fontWeight: "bold",
      fontColor: "#000000",
      marginBottom: 4,
    },
    guestsLine: {
      format: "Guests: {{adults}} adults{{children_text}}{{infants_text}}",
      fontSize: 10,
      fontWeight: "bold",
      fontColor: "#000000",
      marginBottom: 10,
    },
    nightlyRows: {
      format: "{{date}} — Night rate €{{rate}} = €{{total}}",
      fontSize: 10,
      fontWeight: "normal",
      fontColor: "#000000",
      marginBottom: 3,
    },
    cleaningFee: {
      format: "Cleaning fee (one-time): €{{amount}}",
      fontSize: 10,
      fontWeight: "normal",
      fontColor: "#000000",
      marginBottom: 3,
    },
    cityTax: {
      format: "City tax ({{persons}} persons × €{{rate}} × {{nights}} nights): €{{total}}",
      fontSize: 10,
      fontWeight: "normal",
      fontColor: "#000000",
      marginBottom: 15,
    },
  },
  
  // Price Breakdown section
  priceBreakdown: {
    visible: true,
    sectionTitle: {
      text: "Price Breakdown",
      fontSize: 12,
      fontWeight: "bold",
      fontColor: "#000000",
      alignment: "left",
      marginBottom: 10,
    },
    rows: {
      labelFontSize: 10,
      labelFontWeight: "normal",
      labelFontColor: "#000000",
      valueFontSize: 10,
      valueFontWeight: "normal",
      valueFontColor: "#000000",
      marginBottom: 4,
    },
    dividerColor: "#D4AF37",
    dividerWidth: 1,
    subtotal: {
      labelText: "Subtotal",
      labelFontSize: 11,
      labelFontWeight: "bold",
      labelFontColor: "#000000",
      valueFontSize: 11,
      valueFontWeight: "bold",
      valueFontColor: "#000000",
      marginTop: 6,
      marginBottom: 6,
    },
    total: {
      labelText: "TOTAL",
      labelFontSize: 14,
      labelFontWeight: "bold",
      labelFontColor: "#000000",
      valueFontSize: 14,
      valueFontWeight: "bold",
      valueFontColor: "#000000",
      marginTop: 10,
      marginBottom: 0,
    },
  },
  
  // Final Notes section (after Price Breakdown, before Footer)
  finalNotes: {
    visible: true,
    title: {
      visible: true,
      text: "",
      fontSize: 11,
      fontWeight: "bold",
      fontColor: "#000000",
      alignment: "left",
      marginBottom: 8,
    },
    bodyText: {
      visible: true,
      text: "",
      fontSize: 10,
      fontWeight: "normal",
      fontColor: "#000000",
      alignment: "left",
      marginBottom: 15,
    },
  },
  
  // Footer
  footer: {
    visible: true,
    decorativeElement: {
      visible: true,
      type: "ornament", // ornament, line, none
      color: "#000000",
      width: 100,
      marginTop: 40,
    },
  },
};

// Helper to deep merge settings with defaults
export function mergeWithDefaults(savedSettings) {
  if (!savedSettings) return { ...DEFAULT_TEMPLATE_SETTINGS };
  
  // Deep merge
  const merged = JSON.parse(JSON.stringify(DEFAULT_TEMPLATE_SETTINGS));
  
  function deepMerge(target, source) {
    for (const key in source) {
      if (source[key] !== undefined) {
        if (source[key] && typeof source[key] === 'object' && !Array.isArray(source[key])) {
          if (!target[key]) target[key] = {};
          deepMerge(target[key], source[key]);
        } else {
          // Preserve all values including empty strings, null, false, 0
          target[key] = source[key];
        }
      }
    }
  }
  
  deepMerge(merged, savedSettings);
  return merged;
}

// Migration function for old settings format
export function migrateOldSettings(oldSettings) {
  if (!oldSettings) return null;
  
  // Check if already in new format
  if (oldSettings.version === "2.0") return oldSettings;
  
  // Migrate from old format
  const migrated = JSON.parse(JSON.stringify(DEFAULT_TEMPLATE_SETTINGS));
  
  // Map old fields to new structure
  if (oldSettings.header_text) {
    migrated.brandHeader.title.text = oldSettings.header_text;
  }
  if (oldSettings.header_text_size) {
    migrated.brandHeader.title.fontSize = oldSettings.header_text_size;
  }
  if (oldSettings.header_text_bold !== undefined) {
    migrated.brandHeader.title.fontWeight = oldSettings.header_text_bold ? "bold" : "normal";
  }
  
  if (oldSettings.header_subtext) {
    migrated.brandHeader.subtitle.text = oldSettings.header_subtext;
  }
  if (oldSettings.header_subtext_size) {
    migrated.brandHeader.subtitle.fontSize = oldSettings.header_subtext_size;
  }
  
  if (oldSettings.centered_title) {
    migrated.documentTitle.text = oldSettings.centered_title;
  }
  if (oldSettings.centered_title_size) {
    migrated.documentTitle.fontSize = oldSettings.centered_title_size;
  }
  
  if (oldSettings.intro_text) {
    migrated.intro.bodyText.text = oldSettings.intro_text;
  }
  if (oldSettings.body_text_size) {
    migrated.intro.bodyText.fontSize = oldSettings.body_text_size;
  }
  
  if (oldSettings.prepared_for_template) {
    migrated.headerTopRight.preparedLine.text = oldSettings.prepared_for_template.replace("{name}", "").trim() || "Prepared for Mr./Ms.";
  }
  
  if (oldSettings.pdf_font) {
    migrated.layout.defaultFontFamily = oldSettings.pdf_font === "garamond" ? "times" : oldSettings.pdf_font;
  }
  
  // Map other old fields as needed...
  if (oldSettings.stay_details_title_size) {
    migrated.stayDetails.sectionTitle.fontSize = oldSettings.stay_details_title_size;
  }
  if (oldSettings.stay_details_title_bold !== undefined) {
    migrated.stayDetails.sectionTitle.fontWeight = oldSettings.stay_details_title_bold ? "bold" : "normal";
  }
  
  if (oldSettings.price_breakdown_title_size) {
    migrated.priceBreakdown.sectionTitle.fontSize = oldSettings.price_breakdown_title_size;
  }
  
  if (oldSettings.total_size) {
    migrated.priceBreakdown.total.labelFontSize = oldSettings.total_size;
    migrated.priceBreakdown.total.valueFontSize = oldSettings.total_size;
  }
  if (oldSettings.total_bold !== undefined) {
    const weight = oldSettings.total_bold ? "bold" : "normal";
    migrated.priceBreakdown.total.labelFontWeight = weight;
    migrated.priceBreakdown.total.valueFontWeight = weight;
  }
  
  migrated.version = "2.0";
  return migrated;
}
